/**
 * replay.ts — Match replay serialization, deserialization, and local storage management.
 */

import { BotDifficulty } from './bots';
import { Obstacle, ObstacleDensity } from './obstacles';

export interface ReplayTurn {
  teamIndex: number;
  soldierIndex: number;
  expression: string;
  hits: number;
}

export interface ReplaySoldier {
  teamIndex: number;
  x: number;
  y: number;
}

export interface ReplayChatMessage {
  turnIndex: number;
  senderName: string;
  text: string;
  isPlayer: boolean;
}

export interface ReplayData {
  version: number;
  createdAt: number;
  difficulty: BotDifficulty;
  team1Count: number;
  team2Count: number;
  keepTraces: boolean;
  obstacleDensity?: ObstacleDensity;
  showCoordinates?: boolean;
  turnTimer?: number;
  initialSoldiers: ReplaySoldier[];
  initialObstacles?: Obstacle[];
  turns: ReplayTurn[];
  chatLog?: ReplayChatMessage[];
}

const STORAGE_KEY = 'mathillery_recent_replays';
const MAX_LOCAL_REPLAYS = 5;

/**
 * Encodes a ReplayData object into a shareable string code (Base64).
 */
export function exportReplayCode(data: ReplayData): string {
  try {
    const jsonStr = JSON.stringify(data);
    // Use btoa for Base64 encoding (UTF-8 safe)
    const encoded = btoa(encodeURIComponent(jsonStr));
    return encoded;
  } catch (err) {
    console.error('Failed to export replay code:', err);
    return '';
  }
}

/**
 * Decodes a shareable replay code string back into a ReplayData object.
 * Returns null if invalid or corrupted.
 */
export function importReplayCode(code: string): ReplayData | null {
  if (!code || typeof code !== 'string') return null;

  try {
    const trimmed = code.trim();
    const jsonStr = decodeURIComponent(atob(trimmed));
    const parsed = JSON.parse(jsonStr) as ReplayData;

    // Basic structure validation
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      Array.isArray(parsed.initialSoldiers) &&
      Array.isArray(parsed.turns)
    ) {
      return parsed;
    }
    return null;
  } catch (err) {
    return null;
  }
}

// In-memory fallback if localStorage is undefined (e.g. Node tests)
let memoryStore: Record<string, string> = {};

function getStorageItem(key: string): string | null {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(key);
  }
  return memoryStore[key] || null;
}

function setStorageItem(key: string, value: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(key, value);
  } else {
    memoryStore[key] = value;
  }
}

/**
 * Saves a replay to localStorage, keeping only the last 5 matches.
 */
export function saveLocalReplay(data: ReplayData): void {
  try {
    const existing = getRecentReplays();
    // Prepend new replay
    const updated = [data, ...existing.filter(r => r.createdAt !== data.createdAt)].slice(0, MAX_LOCAL_REPLAYS);
    setStorageItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Unable to save local replay:', err);
  }
}

/**
 * Deletes a specific replay from localStorage by createdAt timestamp.
 */
export function deleteLocalReplay(createdAt: number): void {
  try {
    const existing = getRecentReplays();
    const updated = existing.filter(r => r.createdAt !== createdAt);
    setStorageItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Unable to delete local replay:', err);
  }
}

/**
 * Retrieves the list of recent local replays from localStorage.
 */
export function getRecentReplays(): ReplayData[] {
  try {
    const stored = getStorageItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return parsed.filter(item => item && Array.isArray(item.initialSoldiers) && Array.isArray(item.turns));
    }
    return [];
  } catch (err) {
    return [];
  }
}
